import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { QueueItem, QueueItemInsert, QueueItemUpdate } from '../lib/database.types'

const KEY = ['queue_items']

export const QUEUE_STATUSES = ['idea', 'planned', 'printing', 'done', 'dropped'] as const
export const QUEUE_REASONS = ['restock', 'commission', 'try-out', 'personal', 'other'] as const
export const QUEUE_PRIORITIES = ['low', 'med', 'high'] as const

export function useQueueItems() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<QueueItem[]> => {
      const { data, error } = await supabase
        .from('queue_items')
        .select('*')
        .order('status', { ascending: true })
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useCreateQueueItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: Omit<QueueItemInsert, 'user_id'>): Promise<QueueItem> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const { data, error } = await supabase
        .from('queue_items')
        .insert({ ...input, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateQueueItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & QueueItemUpdate): Promise<QueueItem> => {
      const { data, error } = await supabase
        .from('queue_items')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteQueueItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('queue_items').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
